import React, { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthProvider';
import { getDefaultRoute } from '../../../utils/getDefaultRoute';

const Callback: React.FC = () => {
  const { isAuthenticated, isLoading, hasPermission } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // Check if this is an authentication callback (has code/state params)
    const urlParams = new URLSearchParams(location.search);
    const hasAuthCode = urlParams.has('code') || urlParams.has('state');

    if (!isLoading) {
      if (isAuthenticated) {
        // Authentication successful, redirect to intended destination or analytics as fallback
        const intendedDestination = sessionStorage.getItem('intendedDestination');
        if (intendedDestination) {
          sessionStorage.removeItem('intendedDestination');
          navigate(intendedDestination, { replace: true });
        } else {
          const defaultRoute = getDefaultRoute(hasPermission);
          navigate(defaultRoute, { replace: true });
        }
      } else {
        // If we have auth params, authentication might still be processing
        // Wait a bit before redirecting
        if (hasAuthCode) {
          const timer = setTimeout(() => {
            if (!isAuthenticated) {
              navigate('/', { replace: true });
            }
          }, 2000); // Wait 2 seconds for auth to complete

          return () => clearTimeout(timer);
        } else {
          // No auth params, redirect immediately
          navigate('/', { replace: true });
        }
      }
    }
  }, [isAuthenticated, isLoading, navigate, location.search]);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100 dark:from-gray-900 dark:to-gray-800">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <div className="text-gray-600 dark:text-gray-400">Completing authentication...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100 dark:from-gray-900 dark:to-gray-800">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
        <div className="text-gray-600 dark:text-gray-400">Redirecting...</div>
      </div>
    </div>
  );
};

export default Callback;
